import { Config } from '@src/core/Config';
import { wallGunBudget } from '@src/core/Difficulty';
import { spawnGargoyle, spawnSpitter } from '@src/ecs/archetypes';
import { EnemyKind } from '@src/ecs/components';
import type { GameContext } from '@src/ecs/world';
import { DoorKind, GateSide, HALF_H, HALF_W } from '@src/game/arena/RoomLayout';
import { floorMod } from '@src/game/FloorModifiers';

/** Stay this far from a live gate so the doorway stays walkable. */
const GATE_CLEAR = Config.room.gateWidth / 2 + 1.15;
/** Don't plant two guns on top of each other. */
const SLOT_SPACING = 3.4;

export function isWallEnemy(kind: number): boolean {
  return kind === EnemyKind.Gargoyle || kind === EnemyKind.Spitter;
}

/**
 * How many wall guns this room gets. Combat waits until minTier (after imps);
 * hazard pits can debut them a room earlier. Treasure is skip-or-one. Gauntlets
 * are the whole point of the room. Rest rooms and the Champion's Ring have none.
 */
export function wallEnemyCount(tier: number, door: DoorKind, rng: { chance: (p: number) => boolean }): number {
  const g = Config.enemies.gargoyle;
  if (door === DoorKind.Arena || door === DoorKind.Sanctum) return 0;
  if (door === DoorKind.Gauntlet) return Math.min(Config.pools.gargoyles - 1, 4 + Math.floor(tier / 3));
  if (door === DoorKind.Treasure) {
    if (tier < g.minTier) return 0;
    return rng.chance(0.5) ? 1 : 0;
  }
  if (door === DoorKind.Hazard) {
    if (tier < g.hazardMinTier) return 0;
    return Math.min(6, 2 + Math.floor((tier - 2) * 0.7));
  }
  if (tier < g.minTier) return 0;
  if (tier === g.minTier) return rng.chance(0.75) ? 1 : 0;
  if (tier <= 5) return 2;
  if (tier <= 7) return 3;
  return 4;
}

/** Share of this room's wall guns that mount as acid spitters (0 before they debut). */
export function spitterShare(tier: number, door: DoorKind): number {
  const s = Config.enemies.spitter;
  if (tier < s.minTier) return 0;
  return door === DoorKind.Gauntlet ? s.gauntletShare : s.share;
}

interface WallSlot {
  x: number;
  y: number;
  side: GateSide;
  sealed: boolean;
}

function alongSlots(side: GateSide, sealed: boolean): number[] {
  const horiz = side === GateSide.North || side === GateSide.South;
  const half = horiz ? HALF_W : HALF_H;
  const edge = horiz ? 2.2 : 1.6;
  const raw = horiz ? [-11.0, -7.2, -3.6, 3.6, 7.2, 11.0] : [-5.6, -3.0, 3.0, 5.6];
  if (sealed) raw.push(0);
  const out: number[] = [];
  for (let i = 0; i < raw.length; i++) {
    const a = raw[i];
    if (Math.abs(a) > half - edge) continue;
    if (!sealed && Math.abs(a) < GATE_CLEAR) continue;
    out.push(a);
  }
  return out;
}

function mountPos(side: GateSide, along: number): { x: number; y: number } {
  const t = Config.room.wallThickness;
  switch (side) {
    case GateSide.North:
      return { x: along, y: HALF_H - t * 0.22 };
    case GateSide.South:
      return { x: along, y: -HALF_H + t * 0.22 };
    case GateSide.East:
      return { x: HALF_W - t * 0.22, y: along };
    case GateSide.West:
    default:
      return { x: -HALF_W + t * 0.22, y: along };
  }
}

function collectSlots(doorKinds: Uint8Array): WallSlot[] {
  const slots: WallSlot[] = [];
  for (let s = 0; s < 4; s++) {
    const sealed = doorKinds[s] === DoorKind.Sealed;
    const along = alongSlots(s as GateSide, sealed);
    for (let i = 0; i < along.length; i++) {
      const p = mountPos(s as GateSide, along[i]);
      slots.push({ x: p.x, y: p.y, side: s as GateSide, sealed });
    }
  }
  return slots;
}

function tooClose(x: number, y: number, picked: WallSlot[]): boolean {
  const min2 = SLOT_SPACING * SLOT_SPACING;
  for (let i = 0; i < picked.length; i++) {
    const dx = picked[i].x - x;
    const dy = picked[i].y - y;
    if (dx * dx + dy * dy < min2) return true;
  }
  return false;
}

/** Place wall gargoyles (and spitters) for this room. Returns how many spawned. */
export function seedWallEnemies(ctx: GameContext): number {
  const room = ctx.room;
  const rule = floorMod(ctx.floor.modifier);
  let raw = wallEnemyCount(room.tier, room.door, ctx.rng);
  // Fortified floors add guns to any room that already fights with them; Gauntlets are the one
  // room that always gets the bonus. Rest rooms and the ring stay clean.
  if (raw > 0 || room.door === DoorKind.Gauntlet) raw += rule.wallGunBonus;
  const want = wallGunBudget(Math.min(Config.pools.gargoyles, raw));
  if (want <= 0) return 0;
  const spitters = Math.round(want * spitterShare(room.tier, room.door));

  const slots = collectSlots(room.doorKinds);
  // Prefer sealed stone, then shuffle so rooms don't always load the same corners.
  for (let i = slots.length - 1; i > 0; i--) {
    const j = ctx.rng.int(0, i);
    const tmp = slots[i];
    slots[i] = slots[j];
    slots[j] = tmp;
  }
  slots.sort((a, b) => (a.sealed === b.sealed ? 0 : a.sealed ? -1 : 1));

  const weak = room.door === DoorKind.Treasure ? 0.55 : 1;
  const picked: WallSlot[] = [];
  for (let i = 0; i < slots.length && picked.length < want; i++) {
    const s = slots[i];
    if (tooClose(s.x, s.y, picked)) continue;
    // Spitters take the first mounts so the meaner gun tends to sit on sealed stone.
    const spawn = picked.length < spitters ? spawnSpitter : spawnGargoyle;
    if (spawn(ctx, s.x, s.y, weak) < 0) continue;
    picked.push(s);
  }
  return picked.length;
}

/** Inward unit vector from a wall mount (into the room). */
export function wallInward(x: number, y: number): { nx: number; ny: number } {
  const e = HALF_W - Math.abs(x);
  const n = HALF_H - Math.abs(y);
  if (e < n) return x > 0 ? { nx: -1, ny: 0 } : { nx: 1, ny: 0 };
  return y > 0 ? { nx: 0, ny: -1 } : { nx: 0, ny: 1 };
}
